import { UserServiceClient } from 'fitbeat.api-clients';
import { sleep } from 'fitbeat.gym-core';
import { musicPlatform } from 'fitbeat.gym-core/config';
import { CardioOrResistance } from 'fitbeat.models';
import { Platform } from 'react-native';
import {
  ApiConfig,
  ApiScope,
  auth as SpotifyAuth,
  PlayerState,
  remote as SpotifyRemote,
} from 'react-native-spotify-remote';
import { getApiConfig, getConfig } from '../../../config';
import { analyicsForAction } from '../../../services/google-analytics/analyicsForAction';
import { Logger } from '../../../utils/Logger';
import { closeSetupScreen } from '../../actions';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import {
  CONNECTION_STATE_CHANGE,
  FETCH_FB_PLAYLISTS_SUCCESS,
  FETCH_PLAYLISTS_SUCCESS,
  FETCH_SONGS_SUCCESS, FETCH_USER_PLAYLISTS_SUCCESS,
  FETCH_USER_PROFILE_SUCCESS,
  fetchMusicDataFromAsyncStorage,
  SELECTED_PLAYLIST,
} from './actions';
import {
  IMusicPlayerSource, IPlayerState, IPlaylist, ISong,
} from './interfaces';

const MAX_WAIT_FOR_TRACK = 5000;

export class SpotifyMusicSource extends IMusicPlayerSource {
  public async skipNext(): Promise<void> {
    Logger.leaveBreadcrumb('Spotify next');
    await SpotifyRemote.skipToNext();
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async skipPrevious(): Promise<void> {
    Logger.leaveBreadcrumb('Spotify previous');
    await SpotifyRemote.skipToPrevious();
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async pause(): Promise<void> {
    Logger.leaveBreadcrumb('Spotify pause');
    await SpotifyRemote.pause();
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async play(): Promise<void> {
    Logger.leaveBreadcrumb('Spotify play');
    await SpotifyRemote.resume();
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async setShuffling(shuffling: boolean): Promise<void> {
    Logger.leaveBreadcrumb('Spotify shuffle', { shuffling });
    await SpotifyRemote.setShuffling(shuffling);
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async playSong(song: ISong): Promise<void> {
    Logger.leaveBreadcrumb('Spotify playSong');
    await SpotifyRemote.playUri(song.uri);
    this.mapStateAndCallStateCallbackInBackground();
  }

  public async playPlaylist(playlist: IPlaylist): Promise<IPlayerState> {
    try {
      Logger.leaveBreadcrumb('Spotify playPlaylist');
      await SpotifyRemote.playUri(playlist.uri);
      await SpotifyRemote.resume();
      await this.fetchPlaylistTracksFromWeb(playlist.id);
    } catch (e) {
      if (
        !e.message.includes('The operation couldn’t be completed. (com.spotify.app-remote.wamp-client error -3000.)')
      ) {
        throw e;
      }
    }
    return this.mapSpotifyPlayerStateToIPlayerState();
  }

  public async clean(): Promise<void> {
    Logger.leaveBreadcrumb('Spotify.clean');
    await SpotifyRemote.pause();
    SpotifyRemote.removeAllListeners();
    await SpotifyRemote.disconnect();
    await SpotifyAuth.endSession();
    dispatch({ type: CLEAN_MUSIC_STORE });
  }

  public async initialize(): Promise<void> {
    Logger.debug('Spotify initialize...');

    const { homeWorkouts } = this.getAppState();
    const initialPlaylist = await this.getInitialPlaylistToPlay(homeWorkouts.homeWorkoutSession);

    Logger.leaveBreadcrumb('Spotify connecting');
    const playerState = await this.connectToSource(initialPlaylist.playlistId);
    Logger.leaveBreadcrumb('Spotify connected', {
      hasCurrentSong: !!playerState.currentSong,
    });

    this.dispatch({
      type: CONNECTION_STATE_CHANGE,
      payload: { isConnected: true, playerState },
      ...analyicsForAction('workout_music_players_auth_success'),
    });
    this.dispatch(closeSetupScreen(DeviceSetupScreens.Music));
    this.dispatch({
      type: SELECTED_PLAYLIST,
      payload: {
        playlist: {
          id: initialPlaylist.playlistId,
          uri: initialPlaylist.playlistId,
          title: initialPlaylist.playlistTitle,
        },
      },
      ...analyicsForAction('workout_music_playlists_select'),
    });

    Logger.leaveBreadcrumb('Spotify load user', {
      details: 'Using user profile fetch to determine authentication success',
    });
    const userProfile = await this.fetchUserProfile();
    const { product, display_name } = userProfile;
    this.dispatch({ type: FETCH_USER_PROFILE_SUCCESS, payload: { product, displayName: display_name } });

    Logger.leaveBreadcrumb('Spotify load playlists');
    await Promise.all([
      this.loadUserPlaylists(),
      this.fetchFitbeatPlaylists(),
      this.fetchPlaylistTracksFromWeb(initialPlaylist.playlistId),
    ]);

    this.dispatch({
      type: FETCH_PLAYLISTS_SUCCESS,
      ...analyicsForAction('workout_music_playlists_loaded'),
    });
    Logger.leaveBreadcrumb('Spotify initialized');
  }

  public async loadUserPlaylists(): Promise<void> {
    const playlistsUrl = 'https://api.spotify.com/v1/me/playlists';
    const res = await this.fetchFromSpotifyWebAPI(playlistsUrl);

    let userPlaylists: IPlaylist[] = [];
    if (res && res.items) {
      const playlistsWithSongs = res.items.filter((item) => item.tracks && item.tracks.total);
      userPlaylists = playlistsWithSongs.map((item): IPlaylist => {
        const images = item.images || [];
        let imageData = images.find((image) => image.height === 300);
        if (images.length >= 1 && !imageData) {
          imageData = images.reduce((prev, current) => (prev.height > current.height ? prev : current));
        }

        return {
          id: item.id,
          uri: item.uri,
          title: item.name,
          image: imageData ? imageData.url : null,
        };
      });
    }

    Logger.debug('spotify userPlaylists', userPlaylists);
    this.dispatch({
      type: FETCH_USER_PLAYLISTS_SUCCESS,
      payload: userPlaylists,
    });
  }

  private async setInitialShuffleAndPlay() {
    const playerState = await SpotifyRemote.getPlayerState();
    Logger.leaveBreadcrumb('Spotify config shuffle', {
      isShuffling: playerState.playbackOptions.isShuffling,
      isPaused: playerState.isPaused,
    });

    try {
      if (!playerState.playbackOptions.isShuffling) {
        Logger.debug('Spotify setShuffling and skipToNext');
        await SpotifyRemote.setShuffling(true);
        await SpotifyRemote.skipToNext();
      }
    } catch (e) {
      Logger.leaveBreadcrumb('Spotify shuffle failed', {
        details: 'failed to set spotify to shuffle/skip to next track. not critical, ensurePlaying called later',
        error: e,
      });
    }

    try {
      if (playerState.isPaused) {
        Logger.debug('Spotify resume');
        await SpotifyRemote.resume();
      }
    } catch (e) {
      Logger.leaveBreadcrumb('Spotify resume failed', {
        details: 'failed to resume spotify after shuffle setup. not critical, ensurePlaying called later',
        error: e,
      });
    }

    Logger.debug('Spotify setInitialShuffleAndPlay done');
  }

  private async ensurePlaying(initialPlaylist): Promise<void> {
    Logger.debug('Spotify ensurePlaying...');
    const state = await SpotifyRemote.getPlayerState();
    if (state.isPaused || !state.track) {
      Logger.leaveBreadcrumb('Spotify: not playing', {
        state,
        initialPlaylist,
      });
      await SpotifyRemote.playUri(initialPlaylist);
    }
    Logger.debug('Spotify ensurePlaying done', state);
  }

  private mapSpotifyPlayerStateToISong(playerState: PlayerState): ISong {
    return {
      uri: playerState.track.uri,
      id: playerState.track.uri,
      artist: playerState.track.artist.name,
      title: playerState.track.name,
      duration: playerState.track.duration,
    };
  }

  private async waitForSpotifyStateWithTrack(): Promise<PlayerState> {
    Logger.debug('Spotify waitForSpotifyStateWithTrack starting');
    const start = Date.now();

    let spotifyPlayerState = await SpotifyRemote.getPlayerState();
    Logger.debug('Spotify waitForSpotifyStateWithTrack loaded initial', spotifyPlayerState);

    if (!spotifyPlayerState.track) {
      Logger.leaveBreadcrumb('Spotify wait for track');
      const timeoutTime = Date.now() + MAX_WAIT_FOR_TRACK; // timeout from here, after initial state loaded
      while (!spotifyPlayerState.track && Date.now() < timeoutTime) {
        await sleep(20);
        spotifyPlayerState = await SpotifyRemote.getPlayerState();
        Logger.debug('Spotify waiting for track', spotifyPlayerState);
      }

      if (!spotifyPlayerState.track) {
        Logger.notify('Spotify no track loader');
        throw new Error('Failed to load track from spotify');
      }
    }

    Logger.debug(`Spotify waitForSpotifyStateWithTrack done in ${Date.now() - start}msec`);
    return spotifyPlayerState;
  }

  private async mapSpotifyPlayerStateToIPlayerState(): Promise<IPlayerState> {
    const spotifyPlayerState = await this.waitForSpotifyStateWithTrack();
    Logger.leaveBreadcrumb('Spotify mapping state');
    const playlistSongs = this.getAppState().music.songs;
    const songFromPlaylist = playlistSongs.find((song) => song.uri === spotifyPlayerState.track.uri);

    let currentSong: ISong;
    if (songFromPlaylist) {
      currentSong = songFromPlaylist;
    } else {
      currentSong = this.mapSpotifyPlayerStateToISong(spotifyPlayerState);
    }

    const canSkipPrevious = Platform.OS === 'ios'
      ? spotifyPlayerState.playbackRestrictions.canSkipPrevious : spotifyPlayerState.playbackRestrictions.canSkipPrev;

    const playerState = {
      isPaused: spotifyPlayerState.isPaused,
      canSkipNext: spotifyPlayerState.playbackRestrictions.canSkipNext,
      canSkipPrevious,
      canToggleShuffle: spotifyPlayerState.playbackRestrictions.canToggleShuffle,
      isShuffling: spotifyPlayerState.playbackOptions.isShuffling,
      currentSong,
    };
    Logger.debug('Spotify player state', playerState);
    return playerState;
  }

  private mapStateAndCallStateCallbackInBackground() {
    this.mapStateAndCallStateCallback().catch(() => { /* ignored */ });
  }

  private async mapStateAndCallStateCallback(): Promise<void> {
    Logger.debug('Spotify: mapping state and updatePlayerStateCallback');

    try {
      const state = await this.mapSpotifyPlayerStateToIPlayerState();
      this.updatePlayerStateCallback(state);
    } catch (ignored) {
      // no-op
    }
  }

  private async getInitialPlaylistToPlay(homeWorkoutSession: any):
  Promise<{ playlistId: string, playlistTitle: string }> {
    const storedData = await fetchMusicDataFromAsyncStorage();
    if (storedData && storedData.musicPLayer === musicPlatform.Spotify && storedData.playlistUri) {
      return { playlistId: storedData.playlistUri, playlistTitle: storedData.playlistTitle || '' };
    }

    const userServiceClient = new UserServiceClient(getApiConfig(), fetch, null, undefined);
    const fbPlaylists = await userServiceClient.getUserPlatformLink(musicPlatform.Spotify);
    const isBurnWorkout = homeWorkoutSession.workoutSettings.cardioOrResistance === CardioOrResistance.Cardio;
    const isBuildWorkout = homeWorkoutSession.workoutSettings.cardioOrResistance === CardioOrResistance.Resistance;
    return isBurnWorkout ? { playlistId: fbPlaylists.burnPlaylist, playlistTitle: 'FITILLION Burn' }
      : isBuildWorkout ? { playlistId: fbPlaylists.buildPlaylist, playlistTitle: 'FITILLION Build' }
        : { playlistId: fbPlaylists.balancePlaylist, playlistTitle: 'FITILLION Balance' };
  }

  private async fetchFromSpotifyWebAPI(url): Promise<any> {
    const session = await SpotifyAuth.getSession();
    if (session && session.accessToken && session.expirationDate) {
      let { accessToken } = session;
      if ((new Date(session.expirationDate).getTime() - Date.now()) < 60000) {
        accessToken = await this.refreshToken(session);
      }
      const options = {
        method: 'GET',
        headers: new Headers({
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }),
        handle401: false,
      };
      const res = await fetch(url, options);
      if (res.status === 200) {
        return res.json();
      }
    }
  }

  private async connectToSource(initialPlaylist: string): Promise<IPlayerState> {
    const config = getConfig();
    const spotifyConfig: ApiConfig = {
      clientID: config.spotifyClientId,
      redirectURL: config.spotifyRedirectURL,
      tokenRefreshURL: `${config.apiBaseUrl}/api/spotify/refresh?clientId=${config.spotifyClientId}`,
      tokenSwapURL: `${config.apiBaseUrl}/api/spotify/swap?clientId=${config.spotifyClientId}`,
      scopes: [
        ApiScope.AppRemoteControlScope,
        ApiScope.UserFollowReadScope,
        ApiScope.PlaylistReadPrivateScope,
        ApiScope.UserReadPlaybackStateScope,
        ApiScope.UserReadPrivateScope,
        ApiScope.UserReadEmailScope,
      ],
      showDialog: true,
      playURI: initialPlaylist,
    };

    SpotifyRemote.removeAllListeners();
    await SpotifyRemote.disconnect();
    await SpotifyAuth.endSession();

    const newSession = await this.authorize(spotifyConfig);
    Logger.leaveBreadcrumb('Spotify authorized');
    const isConnected = await SpotifyRemote.connect(newSession.accessToken);
    Logger.leaveBreadcrumb('Spotify connect done', { isConnected });

    await this.setInitialShuffleAndPlay();
    await this.ensurePlaying(initialPlaylist);
    return this.mapSpotifyPlayerStateToIPlayerState();
  }

  private async authorize(spotifyConfig: ApiConfig): Promise<any> {
    try {
      return await SpotifyAuth.authorize(spotifyConfig);
    } catch (e) {
      if (e.message === 'Cancelled' && e.code === '500') {
        Logger.leaveBreadcrumb('Spotify auth canceled', e);
        throw new Error('Spotify authorizion cancelled');
      } else {
        Logger.notify(new Error(`Spotify authorize failed: ${e.message}`));
      }

      throw e;
    }
  }

  private async fetchUserProfile() {
    const url = 'https://api.spotify.com/v1/me';
    return this.fetchFromSpotifyWebAPI(url);
  }

  private async fetchPlaylistName(playlistId: string) {
    const url = `https://api.spotify.com/v1/playlists/${playlistId.split(':').pop()}`;
    const res = await this.fetchFromSpotifyWebAPI(url);
    if (res) {
      return res.name;
    }
  }

  private async refreshToken(session) {
    const config = getConfig();
    const url = `${config.apiBaseUrl}/api/spotify/refresh?clientId=${config.spotifyClientId}`;
    const options = {
      method: 'POST',
      handle401: false,
      body: JSON.stringify({
        refresh_token: session.refreshToken,
      }),
      headers: new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    const response = await fetch(url, options);
    if (response.ok) {
      const json = await response.json();
      return json.access_token;
    }
  }

  private async fetchPlaylistTracksFromWeb(playlistId: string) {
    const url = `https://api.spotify.com/v1/playlists/${playlistId.split(':').pop()}`;
    const res = await this.fetchFromSpotifyWebAPI(url);

    let songs: ISong[] = [];
    if (res.tracks) {
      songs = res.tracks.items.map((item): ISong => {
        const thumbnailData = item.track.album.images.find((image) => image.height === 60 || image.height === 64);
        const imageData = item.track.album.images.find((image) => image.height === 300);
        const artists = item.track.artists.map(({ name }) => name).join(', ');
        return {
          uri: item.track.uri,
          id: item.track.uri,
          title: item.track.name,
          artist: artists,
          thumbnail: thumbnailData.url,
          image: imageData.url,
          duration: item.track.duration_ms,
        };
      });
    }

    this.dispatch({
      type: FETCH_SONGS_SUCCESS,
      payload: songs,
    });
    Logger.debug('Spotify fetchFromSpotifyWebAPI', {
      playlistId,
      songs,
    });
  }

  private async getPlaylistImages(playlistId) {
    const url = `https://api.spotify.com/v1/playlists/${playlistId.split(':').pop()}/images`;
    const images = await this.fetchFromSpotifyWebAPI(url);
    if (images.length === 0) {
      return null;
    }

    const imageMatchingDimensions = images.find((image) => image.height === 300);
    if (imageMatchingDimensions) {
      return imageMatchingDimensions.url;
    }
    return images[0].url;
  }

  private async fetchFitbeatPlaylists() {
    try {
      const userServiceClient = new UserServiceClient(getApiConfig(), fetch, null, undefined);
      const fbPlaylists = await userServiceClient.getUserPlatformLink(musicPlatform.Spotify);
      const playlists = await Promise.all(fbPlaylists.playlists.map((playlist) => ({
        uri: playlist.link,
        title: playlist.title,
        images: this.getPlaylistImages(playlist.link),
        id: playlist.link,
      })));
      this.dispatch({
        type: FETCH_FB_PLAYLISTS_SUCCESS,
        payload: {
          playlists,
          defaultBuildBurnPlaylistIds: [
            fbPlaylists.buildPlaylist,
            fbPlaylists.burnPlaylist,
            fbPlaylists.balancePlaylist,
          ],
        },
      });
      Logger.debug('Spotify fetchFitbeatPlaylists', {
        playlists,
      });
    } catch (e) {
      Logger.notify(new Error(`failed to load Fitillion playlists: ${e.message}`));
    }
  }
}
